import { getDocumentObject } from '../common/utils/commonUtils'
import { FLAGS_PARAM_NAME } from '../flags/useFlags'
import { SearchableItemPhase } from '../universalSearch/types'

export const getIsMixpanelDebugEnabled = () => {
  if (getDocumentObject() && typeof localStorage !== undefined) {
    const devFlags = localStorage.getItem(FLAGS_PARAM_NAME)
    return devFlags ? JSON.parse(devFlags).enableMixpanelDebug : false
  }
  return false
}

export const toggleEnableMixpanelDebugWeb = () => {
  if (getDocumentObject() && typeof localStorage !== undefined) {
    const devFlags = localStorage.getItem(FLAGS_PARAM_NAME)
    const enableMixpanelDebug = getIsMixpanelDebugEnabled()

    const newFlags = JSON.parse(devFlags ?? '{}')
    if (enableMixpanelDebug) {
      delete newFlags.enableMixpanelDebug
    } else {
      newFlags.enableMixpanelDebug = true
    }
    localStorage.setItem(FLAGS_PARAM_NAME, JSON.stringify(newFlags))
    window.location.reload()
  }
}

// Universal Search Debug Settings (Web only - uses localStorage)
export interface UniversalSearchDebugSettings {
  autoSearch: boolean
  showDebug: boolean
}

/**
 * Configuration options for Universal Search Fuse.js settings.
 * These can be adjusted in DevTools for testing different search behaviors.
 */
export interface UniversalSearchConfig {
  /** Threshold for standard fuzzy matching (0-1, lower = stricter)*/
  standardThreshold: number
  /** Threshold for strict matching (0-1, lower = stricter)*/
  strictThreshold: number
  minQueryLength: number
  displayNameWeight: number
  keywordsWeight: number
  /** Debounce delay in milliseconds. Default: 500 */
  debounceMs: number
  /** Score difference threshold for treating scores as equal (0 = exact comparison). Default: 0 */
  scoreDiffThreshold: number
  /** Phases that are enabled for search. Items without a matching phase will be filtered out. */
  enabledPhases: SearchableItemPhase[]
}

export const DEFAULT_UNIVERSAL_SEARCH_CONFIG: UniversalSearchConfig = {
  standardThreshold: 0.000999,
  strictThreshold: 0.000599,
  minQueryLength: 3,
  displayNameWeight: 3,
  keywordsWeight: 1,
  debounceMs: 500,
  scoreDiffThreshold: 0,
  enabledPhases: ['P0'],
}

const UNIVERSAL_SEARCH_DEVTOOLS_KEY = 'devtools-universal-search'

interface UniversalSearchDevToolsStorage {
  autoSearch?: boolean
  showDebug?: boolean
  config?: Partial<UniversalSearchConfig>
}

const getUniversalSearchDevToolsStorage = (): UniversalSearchDevToolsStorage => {
  if (getDocumentObject() && typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem(UNIVERSAL_SEARCH_DEVTOOLS_KEY)
    if (stored) {
      try {
        return JSON.parse(stored)
      } catch (error) {
        console.error('Failed to parse Universal Search DevTools storage:', error)
        throw error
      }
    }
  }
  return {}
}

const setUniversalSearchDevToolsStorage = (data: UniversalSearchDevToolsStorage) => {
  if (getDocumentObject() && typeof localStorage !== 'undefined') {
    localStorage.setItem(UNIVERSAL_SEARCH_DEVTOOLS_KEY, JSON.stringify(data))
  }
}

export const getUniversalSearchDebugSettings = (): UniversalSearchDebugSettings => {
  const storage = getUniversalSearchDevToolsStorage()
  return {
    autoSearch: storage.autoSearch ?? false,
    showDebug: storage.showDebug ?? true,
  }
}

export const toggleUniversalSearchAutoSearchWeb = () => {
  const storage = getUniversalSearchDevToolsStorage()
  const currentSettings = getUniversalSearchDebugSettings()
  storage.autoSearch = !currentSettings.autoSearch
  setUniversalSearchDevToolsStorage(storage)
}

export const toggleUniversalSearchShowDebugWeb = () => {
  const storage = getUniversalSearchDevToolsStorage()
  const currentSettings = getUniversalSearchDebugSettings()
  storage.showDebug = !currentSettings.showDebug
  setUniversalSearchDevToolsStorage(storage)
}

/**
 * Gets the Universal Search DevTools config from localStorage (web only).
 * Returns only explicitly set values or undefined if nothing is set.
 */
export const getUniversalSearchConfig = (): Partial<UniversalSearchConfig> | undefined => {
  const storage = getUniversalSearchDevToolsStorage()
  const config = storage.config
  if (config && Object.keys(config).length > 0) {
    return config
  }
  return undefined
}

/**
 * Sets the Universal Search DevTools config in localStorage (web only).
 */
export const setUniversalSearchConfigWeb = (config: UniversalSearchConfig) => {
  const storage = getUniversalSearchDevToolsStorage()
  storage.config = config
  setUniversalSearchDevToolsStorage(storage)
}

/**
 * Resets the Universal Search DevTools config in localStorage (web only).
 */
export const resetUniversalSearchConfigWeb = () => {
  const storage = getUniversalSearchDevToolsStorage()
  delete storage.config
  setUniversalSearchDevToolsStorage(storage)
}

/**
 * Checks if any Universal Search DevTools config values are set.
 * Used by DevTools modal to indicate when manual values are being used.
 */
export const hasUniversalSearchConfigOverrides = (): boolean => {
  const config = getUniversalSearchConfig()
  return config !== undefined && Object.keys(config).length > 0
}
